import JsBarcode from 'jsbarcode';
import {
  LABEL_BARCODE_WIDTH_MM,
  LABEL_DOTS_PER_MM,
  LABEL_PRINT_HEIGHT_MM,
  mmToDots,
} from './inventoryLabelGeometry';

export type BarcodeSymbology = 'CODE128' | 'CODE39';

/**
 * Barcode panel, top to bottom (mm). One text line, so the bars get the rest of the strip:
 *
 *   edge 1 | código + peso caps 1.5 | gap 0.6 | bars 6 | edge 0.9
 *
 * The text is spaced by cap height: the comma in the weight hangs into the gap. The bars
 * sit near the bottom edge on purpose: a strip printed slightly off still scans with
 * shorter bars, while clipped text no longer reads.
 */

/** Room above the código + peso caps. */
export const LABEL_TOP_PAD_MM = 1;
/** Código + peso font size; Times caps print ~1.4 mm tall. */
export const LABEL_META_FONT_PT = 6;
/** Cap height reserved for código + peso (a bit over the 6 pt caps). */
export const LABEL_META_CAP_HEIGHT_MM = 1.5;
/** Room under the baseline for the comma in the weight: 3 printer dots. */
export const LABEL_TEXT_DESCENT_MM = 0.375;
/** Meta row (código + peso) PNG height: caps plus descent. */
export const LABEL_META_HEIGHT_MM = LABEL_META_CAP_HEIGHT_MM + LABEL_TEXT_DESCENT_MM;
/** Air between the caps and the bars; also holds the comma. */
export const LABEL_META_TO_BAR_GAP_MM = 0.6;
export const LABEL_BARCODE_TOP_MM =
  LABEL_TOP_PAD_MM + LABEL_META_CAP_HEIGHT_MM + LABEL_META_TO_BAR_GAP_MM;
/** Printed bar height: taller bars scan without careful aiming. Same on every label. */
export const LABEL_BARCODE_HEIGHT_MM = 6;
export const LABEL_BARCODE_HEIGHT_DOTS = mmToDots(LABEL_BARCODE_HEIGHT_MM);
/** Blank between the bars and the bottom edge of the strip. */
export const LABEL_BARCODE_BOTTOM_PAD_MM =
  LABEL_PRINT_HEIGHT_MM - LABEL_BARCODE_TOP_MM - LABEL_BARCODE_HEIGHT_MM;

/** Gap between the barcode panel's left edge and the first bar. */
export const LABEL_PANEL_INSET_MM = 0.5;
/**
 * Blank after the last bar. CODE128 asks for ≥10 modules (~1.7 mm here); the left side
 * already has the 5 mm fold after Plata/Bs, so the PNG itself carries no margin.
 */
export const LABEL_BARCODE_QUIET_ZONE_MM = 2;
/** Widest the bars may print; also the width of the código + peso line. */
export const LABEL_BARCODE_MAX_WIDTH_MM =
  LABEL_BARCODE_WIDTH_MM - LABEL_PANEL_INSET_MM - LABEL_BARCODE_QUIET_ZONE_MM;

/**
 * Bar module width under test with the shop's scanner:
 * - standard: ~1.37 printer dots, the ~15 mm wide 1645/1 the shop validated; the driver
 *   rounds each bar to 1 or 2 dots, so widths come out uneven.
 * - thin: exactly 1 printer dot (0.125 mm); every bar prints even, but thinner.
 */
export const LABEL_BARCODE_VARIANTS = ['standard', 'thin'] as const;
export type LabelBarcodeVariant = (typeof LABEL_BARCODE_VARIANTS)[number];
export const DEFAULT_LABEL_BARCODE_VARIANT: LabelBarcodeVariant = 'standard';

const LABEL_BARCODE_MODULE_MM: Record<LabelBarcodeVariant, number> = {
  standard: 0.171,
  thin: 1 / LABEL_DOTS_PER_MM,
};
/** PNG pixels per module; the thin variant rasters 1:1 to printer dots. */
const LABEL_BARCODE_RASTER_MODULE_PX: Record<LabelBarcodeVariant, number> = {
  standard: 2,
  thin: 1,
};

/**
 * Text keeps this far from the strip's right edge. The shop's printer lands the image
 * ~2 mm right of the layout and label printing commonly allows ±1.6 mm, so a weight ending
 * at the 86 mm quiet-zone line lost its decimals (7,4 printed as "7.").
 */
export const LABEL_TEXT_SAFE_EDGE_MM = 4;
/** Widest the código + peso line may run: up to 84 mm on the 88 mm strip. */
export const LABEL_META_MAX_WIDTH_MM =
  LABEL_BARCODE_WIDTH_MM - LABEL_PANEL_INSET_MM - LABEL_TEXT_SAFE_EDGE_MM;

/** Peso ends on the last bar, or at the safe edge when the bars run past it. */
export function labelMetaWidthMm(barWidthMm: number): number {
  return barWidthMm > 0 ? Math.min(barWidthMm, LABEL_META_MAX_WIDTH_MM) : LABEL_META_MAX_WIDTH_MM;
}

/** Printed width of a barcode with this many modules; long codes shrink to fit the panel. */
export function labelBarcodeWidthMm(modules: number, variant: LabelBarcodeVariant): number {
  return Math.min(modules * LABEL_BARCODE_MODULE_MM[variant], LABEL_BARCODE_MAX_WIDTH_MM);
}

const BARCODE_ASPECT_RATIO = 'xMidYMid meet';

/**
 * Renders CODE128 (default) onto an SVG element. Value must match productCodeUnique for scanners.
 */
export function renderBarcodeSvg(
  svgEl: SVGSVGElement | null,
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): void {
  if (svgEl == null) return;
  // Clear before the blank check so a cleared value also clears the rendered barcode.
  while (svgEl.firstChild) {
    svgEl.removeChild(svgEl.firstChild);
  }
  if (value.trim() === '') return;
  try {
    JsBarcode(svgEl, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.85,
      height: options?.height ?? 28,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    svgEl.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  } catch {
    /* invalid data for symbology — leave svg empty */
  }
}

/** Inline SVG string (preview / fallback). */
export function barcodeToSvgString(
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): string {
  if (!value?.trim()) return '';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.75,
      height: options?.height ?? 26,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return '';
  }
  svg.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  return svg.outerHTML;
}

export interface LabelBarcodePng {
  dataUrl: string;
  /** Printed width; show the PNG at exactly this width and LABEL_BARCODE_HEIGHT_MM. */
  widthMm: number;
}

/**
 * Raster CODE128 PNG for Chrome → ZDesigner. Avoids SVG print blur on thermal.
 * Rastered at the printed bar height in dots, so CSS only scales it horizontally.
 */
export function labelBarcodePng(
  value: string,
  variant: LabelBarcodeVariant,
): LabelBarcodePng | null {
  if (!value?.trim()) return null;
  const modulePx = LABEL_BARCODE_RASTER_MODULE_PX[variant];
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width: modulePx,
      height: LABEL_BARCODE_HEIGHT_DOTS,
      displayValue: false,
      // No margin: the image edge is the first/last bar. The quiet zone comes from the
      // layout (the fold on the left, LABEL_BARCODE_QUIET_ZONE_MM on the right).
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return null;
  }
  return {
    dataUrl: canvas.toDataURL('image/png'),
    widthMm: labelBarcodeWidthMm(canvas.width / modulePx, variant),
  };
}
